export const APP_NAME = 'Compass'
// Custom URL scheme used for deep links + OAuth redirects. Must match
// `expo.scheme` in apps/mobile/app.json and the SSO redirect URL registered
// in the Clerk Dashboard. Single source of truth — never hardcode the literal.
export const APP_SCHEME = 'heycompass'
export const APP_TAGLINE = 'The trips you keep saving.'
// Instagram bot account the IG-DM save flow + onboarding demo point at.
// Bare @username (NO leading @) — call sites add their own `@` / URL prefix.
// Single source of truth — never hardcode the literal; DM copy, deep links,
// and the onboarding mock all derive from this.
export const IG_BOT_HANDLE = 'hey.compass'
// APP_STORE_URL / PLAY_STORE_URL live in their own zero-dep leaf module
// (`./store-urls`) so the web marketing bundle can deep-import them without the
// barrel's zod/constant-corpus bloat. Re-exported by `constants/index.ts`, so
// `import { APP_STORE_URL } from '@verso/shared'` still resolves here.
export const API_VERSION = 'v1'
export const DEFAULT_CURRENCY = 'INR'
export const CURRENCY_SYMBOL = '₹'
export const MAX_PER_PAGE = 50
export const DEFAULT_PER_PAGE = 20

/**
 * Explore country filter entries. `isoCode` is the ISO 3166-1 alpha-2 code
 * used to render the crisp SVG flag via the mobile `<CountryFlag>` component.
 * The special 'all' entry has a null isoCode — consumers render a generic
 * globe icon for it.
 */
export const EXPLORE_COUNTRIES = [
  { code: 'all', name: 'All', isoCode: null },
  { code: 'JP', name: 'Japan', isoCode: 'JP' },
  { code: 'IN', name: 'India', isoCode: 'IN' },
  { code: 'ID', name: 'Bali', isoCode: 'ID' },
  { code: 'TH', name: 'Thailand', isoCode: 'TH' },
  { code: 'IT', name: 'Italy', isoCode: 'IT' },
  { code: 'GR', name: 'Greece', isoCode: 'GR' },
  { code: 'VN', name: 'Vietnam', isoCode: 'VN' },
  { code: 'LK', name: 'Sri Lanka', isoCode: 'LK' },
  { code: 'MV', name: 'Maldives', isoCode: 'MV' },
  { code: 'MA', name: 'Morocco', isoCode: 'MA' },
  { code: 'TR', name: 'Turkey', isoCode: 'TR' },
] as const

export const TRIP_CATEGORIES = [
  'adventure',
  'cultural',
  'relaxation',
  'romantic',
  'family',
] as const

export const AI_MODELS = {
  SONNET: 'claude-sonnet-4-6',
  // The companion's model (Agent D). Moved off Haiku 4.5 on 2026-09-02: the
  // Tanzania replay graders kept flagging two Haiku-shaped misses (it guessed
  // the positioning hop instead of searching it, and it did arithmetic across
  // two searches in prose). `browser/agent-loop.ts` already ran this id live.
  SONNET_5: 'claude-sonnet-5',
  HAIKU: 'claude-haiku-4-5-20251001',

  // ── Itinerary generation tiers ──
  // Outline: Haiku. Shadow-tested 4/5 wins vs Sonnet on explicit-request adherence
  // (day-of-week constraints, stated vibes, city assignments). Sonnet's extra
  // creativity sometimes overrode user requests, costing UX trust; Haiku follows
  // instructions more literally which is what we want for the outline shape.
  ITINERARY_OUTLINE: 'claude-haiku-4-5-20251001',
  // Day details: Sonnet 4.6 (bumped 2026-05-24 from Haiku 4.5). Haiku was
  // chosen for cost/latency in the parallel-batched path, but the 2026-05-24
  // baseline showed widespread small-model failures: hallucinated places at
  // wrong coords (teamLab at Tokyo coords during Osaka day), intra-batch
  // duplicates (Dotonbori 3x), generic filler over the user's saves (11%
  // utilization). Sonnet is ~3-5x cost but materially better on structured
  // multi-day generation. See the post-fix baseline report for the delta.
  ITINERARY_DAY_DETAIL: 'claude-sonnet-4-6',
  // Fallback: same model — Sonnet rarely throws; a single retry is enough.
  ITINERARY_FALLBACK: 'claude-sonnet-4-6',

  // ── Itinerary v2 (names + bulk resolver, rip+replace landed 2026-05-26) ──
  // Structure: ONE Sonnet 4.6 call returns trip name + cities + days + place
  // names only (no coords/categories/costs/durations). Replaces v1's outline +
  // per-city day-detail (~4-7 calls). Sonnet chosen for structural coherence;
  // iter 2 may A/B Gemini-2.5-Flash if Sonnet is overkill for the new contract.
  ITINERARY_V2_STRUCTURE: 'claude-sonnet-4-6',
  // Rescue model for the v2 structure call: when ITINERARY_V2_PROVIDER routes the
  // primary call to a cheaper OpenRouter model, the final Zod-retry falls back to
  // Sonnet so a flaky cheap-model run never fails a user's trip. Kept distinct from
  // ITINERARY_V2_STRUCTURE so the default-path model and the rescue model can't drift.
  ITINERARY_V2_STRUCTURE_FALLBACK: 'claude-sonnet-4-6',
  // Enrichment: ONE Gemini 2.5 Flash bulk call per trip with all unknown place
  // names. Returns canonical_title + category + brief_description + Google
  // search hint per name. Cheap + fast for bulk batches.
  ITINERARY_V2_ENRICHMENT: 'gemini-2.5-flash',
  // Persona distill: Haiku 4.5 one-shot per user, output is a ~200-char blurb
  // (negative_affinities + style hints). KV-cached 30d. Trimmed v1's ~1.5K
  // token PALETTE+PERSONA+TASTE-PROFILE down to ~100 tokens at prompt time.
  ITINERARY_V2_PERSONA: 'claude-haiku-4-5-20251001',
} as const

export const INVITE_EXPIRY_DAYS = 7
export const MAX_TRIP_DURATION_DAYS = 30
// Max cities an Agent D trip-plan can carry. The single SoT for the cities-step
// cap: the pick-request Zod bound (`agentDPlanStepPickRequestSchema`), the
// server merge slice (`plan-step-merge.ts`), and the mobile cities-editor add
// cap (`PlanStepCard.tsx`) all reference this so they can never drift.
export const MAX_PLAN_CITIES = 15

// Ghost-user (unlinked IG DM prospect) lifetime caps. Lifetime, not per-day.
// Full extraction runs for up to GUEST_REEL_CAP reels per IGSID; after that,
// every share returns the sign-up gate. GUEST_MESSAGE_CAP bounds outbound DMs
// (bot replies to questions, greetings, cap gates) across the full session.
export const GUEST_REEL_CAP = 3
export const GUEST_MESSAGE_CAP = 25

/**
 * The WhatsApp ghost's save cap, raised from Instagram's 3 (owner ruling
 * 2026-09-03: "anyone should be able to message our bot").
 *
 * SEPARATE FROM `GUEST_REEL_CAP` ON PURPOSE. That constant is read by the
 * Instagram DM webhook as well, and the two channels are not the same product:
 * Instagram's ghost arrives from a public account with a sign-up gate behind
 * it, while a WhatsApp stranger has messaged a number directly and is the
 * population this opening is FOR. Raising the shared constant would silently
 * widen Instagram's spend too.
 *
 * IT IS STILL A CAP, and it has to be. A ghost has no account and no way to be
 * billed, and each save is a real extraction (Apify + Gemini + Claude + Places),
 * so an uncapped stranger path is an open, unauthenticated door onto metered
 * vendors. Lifetime per number.
 */
export const WA_GUEST_REEL_CAP = 25
// Sentinel clerk_id prefix for ghost users. Preserves the users.clerk_id
// NOT NULL + UNIQUE invariants until the user signs up. Never submitted to Clerk.
// Ghosts are intentionally never expired — D1 storage per ghost is tiny and
// a returning prospect months later sees their original saves intact.
export const GUEST_CLERK_ID_PREFIX = 'ig_ghost:'
// The WhatsApp sibling, minted by `services/whatsapp-ghost.service.ts` when an
// unlinked number shares a link into a group.
//
// A DISTINCT PREFIX, not a reuse of the one above, and the reason is
// `platform_users.platform_user_id`: that column is a PRIMARY KEY, i.e. ONE
// GLOBAL NAMESPACE shared by Instagram IGSIDs and E.164 digits (migration 0172
// says so in its own header). The sentinel is the only place the two identity
// kinds are told apart in the `users` row itself, so a shared prefix would make
// `ig_ghost:919876543210` and `wa_ghost:919876543210` the same string and let a
// phone number and an IGSID collide on a UNIQUE column.
export const GUEST_CLERK_ID_PREFIX_WA = 'wa_ghost:'

// Every sentinel prefix a ghost `users.clerk_id` can carry. Listed rather than
// pattern-matched (`/_ghost:/`) so adding a channel means editing this table -
// and so `isGhostClerkId` below cannot silently start matching a real Clerk id
// that happens to contain the substring.
export const GHOST_CLERK_ID_PREFIXES = [
  GUEST_CLERK_ID_PREFIX,
  GUEST_CLERK_ID_PREFIX_WA,
] as const

/**
 * ONE answer to "is this `users.clerk_id` a ghost sentinel rather than a real
 * Clerk id".
 *
 * It exists because the question already had several hand-rolled answers -
 * `clerk_id NOT LIKE 'ig_ghost:%'` in `tools/migrate-clerk-dev-to-prod.ts`,
 * `row.clerk_id.startsWith('ig_ghost:')` in two backfill tools - each of which
 * names ONE prefix and is therefore blind to the second the moment it exists.
 * Those three are all paired with an `is_guest` test that already excludes a
 * WhatsApp ghost, so none of them is wrong today; this is the definition they
 * converge on rather than each growing a second literal.
 *
 * `is_guest` remains the authoritative column - a ghost promoted by
 * `promoteGhostToClerkUser` gets a real `clerk_id` AND `is_guest = false` in the
 * same UPDATE - so this predicate is a provenance read, never a substitute for
 * the flag.
 */
export const isGhostClerkId = (clerkId: string | null | undefined): boolean =>
  !!clerkId && GHOST_CLERK_ID_PREFIXES.some((prefix) => clerkId.startsWith(prefix))

export const EXPLORE_ACTIVITY_TAGS = [
  'cafe',
  'temple',
  'beach',
  'market',
  'museum',
  'restaurant',
  'hotel',
  'viewpoint',
  'park',
  'experience',
] as const
